import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

import AIReportDashboard from "../components/AIReportDashboard";
import { getSharedWorkflow } from "../api/api";
import { isReportData, sanitizeReportData } from "../utils/reportUtils";

export default function SharedWorkflow() {
  const { shareId } = useParams();
  const navigate = useNavigate();

  const [entry, setEntry] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  function cleanJSONResponse(response) {
    if (!response) return null;

    let cleaned = response.trim()
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/\s*```$/i, '');

    try {
      return JSON.parse(cleaned);
    } catch (err) {
      console.error("Invalid JSON after cleaning:", err);
      return null;
    }
  }

  useEffect(() => {
    const fetchSharedEntry = async () => {
      try {
        const res = await getSharedWorkflow(shareId);

        if (res.responsecode !== 200) 
          throw new Error("Failed to fetch shared entry");

        const parsed = cleanJSONResponse(res.entry.response);
        const sanitized = isReportData(parsed) ? sanitizeReportData(parsed) : parsed;

        setSearchResults(sanitized);
        setEntry(res.entry);
      } catch (err) {
        console.error(err);
        setError("This workflow is no longer available.");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedEntry();
  }, [shareId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <Loader2 className="animate-spin w-10 h-10 text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-white px-4">
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex flex-col items-center px-4 sm:px-6">
      {/* Logo at the top with fallback */}
     

      <main className="max-w-5xl w-full flex flex-col gap-8 pb-12">
        {/* Heading with fade-in animation */}
        {/* CTA Section with slide-up animation */}

        <br/>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <motion.img
          src="/tuly.png"
          alt="Tuly Logo"
          className="h-10 w-auto object-contain"
        />
          <div>
            <h2 className="text-lg font-semibold text-blue-800">
              Want to create and share your own workflows?
            </h2>
            <p className="text-blue-700 mt-1">
              Sign up now and start building workflows that your team can collaborate on instantly.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate("/signup")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold shadow-md transition"
            >
              Get Started
            </button>
          </div>
        </motion.div>


        {/* Workflow content with animation */}
        {searchResults ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <AIReportDashboard reportData={searchResults} onBackToSearch={() => {}} />
          </motion.div>
        ) : (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-gray-500 text-center"
          >
            No workflow data available.
          </motion.p>
        )}

      </main>
    </div>
  );
}
